<script setup lang="ts">
import type { BlogPage } from '~~/types/api/page/BlogPage'
import { BLOG_PAGE } from '~~/helpers/types'
import { extractParamFromQuery } from '~~/helpers/extractParamFromQuery'

definePageMeta({
  middleware: ['go-to-page-one'],
})

const route = useRoute()
const { locale } = useI18n()

const slug = Array.isArray(route.params.slug) ? route.params.slug[0] : route.params.slug
const asyncDataCacheKey = computed(() => `fetch-tag-${slug}`)
const { data: tagContent, pending, error } = await useAsyncData(asyncDataCacheKey.value, async () => {
  const { results } = await useApi().tag.getTagBySlug(slug)
  return results[0]
})
if (error.value) {
  throw error.value
}

const page = ref(queryValueToNumber(route.query.page) || 1)
const asyncDataContentCacheKey = computed(() => `fetch-tag-content-${slug}-${route.query.page}`)
const pageSize = 21
const { data, error: errorQueryPage } = await useAsyncData(asyncDataContentCacheKey.value, async () => {
  page.value = queryValueToNumber(route.query.page) || 1
  if (import.meta.client) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  const { results, count } = await useApi().page.getPagesBySlugTag<BlogPage>(slug, {
    type: BLOG_PAGE,
    order: '-first_published_at',
    page: page.value,
    page_size: pageSize,
    locale: getTopLevelLocale(locale.value),
  })

  return { results, count }
}, { watch: [asyncDataContentCacheKey] })
if (errorQueryPage.value) {
  throw errorQueryPage.value
}

useHead(usePageHead({ pageContent: tagContent, addSearchParams: ['page'] }))
</script>

<template>
  <div>
    <LazyAppPageLoading v-if="pending" />
    <template v-else>
      <section class="relative">
        <LazyAppBreadcrumb
          :crumbs="tagContent?.breadcrumb_data.slice(0, 1) || []"
          :current-title="tagContent?.name"
        />
      </section>

      <LazyAppHeaderPage>
        <template #title>
          {{ tagContent?.name }}
        </template>
      </LazyAppHeaderPage>

      <div
        v-if="data"
        class="container relative mb-8"
      >
        <LazyAppPostCardList
          :posts="data.results"
          class="mb-12"
        />

        <div v-if="data.count > pageSize">
          <LazyYscPagination
            :page-size="pageSize"
            :count="data.count"
          />
        </div>
      </div>
    </template>
  </div>
</template>
